import React from "react";
import { AtSignIcon, UsersIcon } from "lucide-react";
import { Avatar, Badge } from "@/components/ui";
import { SocialMentionData } from "@/services/bedrock/dtos/Post";
import { extractInitials } from "@/utils";
import { SocialMentionBadge } from "./SocialMentionBadge";
import { useSearchContext } from "../SearchContext";
import { CAPTION_EDITING_SOCIAL_NETWORKS } from "@/interfaces";

interface AppliedMentionsProps {
  mentions: SocialMentionData[];
  isPosted?: boolean;
  onRemoveMention: (mention: SocialMentionData) => void;
}

export function AppliedMentions({ mentions, isPosted, onRemoveMention }: AppliedMentionsProps) {
  const { players } = useSearchContext();
  const showPlayerGrouping = players.length > 1;

  const { sharedMentions, playerSpecificMentions } = React.useMemo(() => {
    const shared: SocialMentionData[] = [];
    const specific: Record<number, SocialMentionData[]> = {};

    mentions.forEach(m => {
      const playerId = (m as any).playerId;

      if (playerId === null || playerId === undefined) {
        shared.push(m);
      } else {
        if (!specific[playerId]) specific[playerId] = [];
        specific[playerId].push(m);
      }
    });

    return { sharedMentions: shared, playerSpecificMentions: specific };
  }, [mentions]);

  const handleRemoveShared = (mention: SocialMentionData) => {
    // Pass null/undefined as playerId to the remove handler for bulk mentions
    onRemoveMention(mention);
  };

  return (
    <div className="flex flex-col gap-4 bg-slate-100/50 rounded-2xl p-6 border border-slate-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AtSignIcon className="size-4 text-primary" />
          <span className="text-sm font-bold text-slate-900">Applied Mentions</span>
        </div>
        <Badge variant="outline" className="bg-white">
          {mentions.length}
        </Badge>
      </div>

      {mentions.length > 0 ? (
        <div className="flex flex-col gap-6">
          {/* Shared Mentions Section */}
          {sharedMentions.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5">
                <UsersIcon className="size-3.5 text-primary" />
                <span className="text-[11px] font-bold text-primary uppercase tracking-wider">
                  Applied to All Players
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {sharedMentions.map(mention => (
                  <SocialMentionBadge
                    key={`shared-${mention.socialNetworkId}-${mention.externalId}`}
                    mention={mention}
                    isLocked={isPosted && !CAPTION_EDITING_SOCIAL_NETWORKS.includes(mention.socialNetworkId)}
                    onRemove={() => handleRemoveShared(mention)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Player Specific Sections */}
          {Object.entries(playerSpecificMentions).map(([playerId, playerMentions]) => {
            const player = players.find(p => p.playerId === Number(playerId));
            if (playerMentions.length === 0) return null;

            return (
              <div key={playerId} className="flex flex-col gap-2">
                {showPlayerGrouping && (
                  <div className="flex items-center gap-2">
                    <Avatar className="size-5">
                      <Avatar.Image src={player?.photoUrl || undefined} />
                      <Avatar.Fallback className="text-[8px]">{extractInitials(player?.name)}</Avatar.Fallback>
                    </Avatar>
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      {player?.name || "Unknown Player"}
                    </span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {playerMentions.map((mention: SocialMentionData) => (
                    <SocialMentionBadge
                      key={`${mention.socialNetworkId}-${mention.externalId}-${playerId}`}
                      mention={mention}
                      isLocked={isPosted && !CAPTION_EDITING_SOCIAL_NETWORKS.includes(mention.socialNetworkId)}
                      onRemove={() => onRemoveMention(mention)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-4 flex items-center justify-center border-2 border-dashed border-slate-200 rounded-xl opacity-50">
          <p className="text-xs text-slate-500 italic">
            No mentions added yet. Use the search to find and add profiles.
          </p>
        </div>
      )}
    </div>
  );
}
